/**
 * Onboarding tours (TOADO-971): what a tour is, the tours there are, and
 * what each one offers the person given how far they got.
 *
 * A tour is a list of steps, each pointing at a control on the page through
 * `data-tour-anchor`. Progress is the person's, stored by the server (see
 * `TourProgress` in @vd/types); reading a step never sets anything up.
 *
 * A step that asks for a click can be met two ways (Jason, 28 Oct): the
 * person's own click, or Next, which clicks the same control for them. A
 * step can also take the person to its own page as the tour reaches it
 * (`open`), and close what it opened as the tour leaves (`nextClicks`).
 */
import { matchPath } from 'react-router-dom';
import type { TourProgress } from '@vd/types';

export interface TourStep {
  /** The `data-tour-anchor` value of the control the step points at. */
  anchor: string;
  title: string;
  /**
   * What it says. `{selected}` is the name of the chosen option in the step's
   * control, as it is now (Jason, 28 Oct): the view the person is on.
   */
  body: string;
  /**
   * The route the step belongs on, as a React Router pattern. Away from it
   * the tour pauses, and coming back resumes it.
   */
  route?: string;
  /** Said while the tour waits for the person to reach `route`, before the step was ever shown. */
  routeHint?: string;
  /**
   * What the step waits for before Next: the person's own click on the
   * control (`click`), or an event a real control dispatches on the window
   * (`tour:` CustomEvents). Never a click the tour makes.
   */
  /**
   * For an event, `present` names a control whose being on the page already
   * meets it (TOADO-988): the debugging window open before the step began.
   * For a click (Jason, 28 Oct), `click` may name the one control inside the
   * step's that meets it (Board, in the view switcher), and `ignore` a
   * selector a click that does not count matches (the view already chosen).
   */
  waitFor?: { click: true | string; ignore?: string } | { event: `tour:${string}`; present?: string } | { route: string };
  /**
   * Moves on by itself once `waitFor` is met (TOADO-988): for a click that
   * takes the person somewhere the step's route is not, such as a card that
   * opens its ticket or a link to settings, where Next would be out of reach.
   */
  advanceWhenDone?: true;
  /**
   * Shown while a modal is open (TOADO-988): the step's control is in the
   * ticket or the debugging window. Every other step keeps out of a modal's way.
   */
  overModal?: true;
  /** A drawing above the words, from `TourArt`. */
  art?: TourArtName;
  /**
   * The steps that only make sense together (TOADO-988): the sample ticket
   * and what is shown in it. Skipping one skips them all, so a board without
   * a sample does not strand the tour on a ticket it cannot open.
   */
  section?: string;
  /** About something only a workspace owner has (the widget, Jira export): left out for anybody else. */
  ownerOnly?: true;
  /**
   * Making the workspace's first project (Jason, 28 Oct): left out for a
   * workspace that has one, and never gone back to once it has.
   */
  firstProject?: true;
  /** For a step in a window: how to open the window again if it is closed meanwhile (TOADO-988). */
  reopenHint?: string;
  /**
   * About nothing on the page (Jason, 28 Oct): the box sits in the middle,
   * with no ring, and its `anchor` is never looked for.
   */
  centered?: true;
  /**
   * The page the tour takes the person to as it reaches this step, by Next,
   * Back or a skip, and when it is resumed: a path, with `:projectId` and
   * `:ticketId` filled in by the tour (Jason, 28 Oct). A project settings
   * section is named by its `?tab=`.
   */
  open?: string;
  /**
   * A control Next clicks: on a step waiting for a click, the one that meets
   * it, where that is not the step's own control (Table, in the views); on a
   * step already met, one that closes what it opened (Filter's Cancel).
   */
  nextClicks?: string;
  /** A control Back clicks before going back, to undo what the step before opened. */
  backClicks?: string;
  /** What the step says while it waits, where Next cannot do it all: a name to type first (Jason, 28 Oct). */
  waitingHint?: string;
}

/** The drawings a step can show (TOADO-988), each reusing one the app already draws. */
export type TourArtName = 'capture' | 'session' | 'route' | 'widget' | 'agent' | 'extension' | 'sources';

export interface TourDefinition {
  id: string;
  /** Raised whenever steps change meaning: an older stored version is replayed, not resumed. */
  version: number;
  title: string;
  /** Where a tour in progress may resume: anywhere, in its workspace, or on its project. */
  scope: 'none' | 'workspace' | 'project';
  steps: readonly TourStep[];
  /**
   * Where the tour is offered on its own, once, to someone who has never
   * started or declined it (TOADO-981): a route pattern, and what the offer
   * says. Without it the tour is only ever started from Get started.
   */
  offer?: { route: string; body: string };
  /**
   * Makes the sample ticket on the first board it reaches and takes it away
   * when it ends, finished, skipped or closed (TOADO-988, Jason 28 Oct).
   */
  sample?: boolean;
}

/** The step after `step`'s section, or the next step when it has none (TOADO-988). */
export function afterSection(tour: TourDefinition, step: number): number {
  const section = tour.steps[step]?.section;
  if (!section) return step + 1;
  let next = step + 1;
  while (next < tour.steps.length && tour.steps[next]!.section === section) next += 1;
  return next;
}

/** Who is taking a tour, for the steps that are not everybody's (TOADO-988; Jason, 28 Oct). */
export interface TourAudience {
  /** An owner of the tour's workspace. */
  owner: boolean;
  /** The workspace has a project, so its first one need not be made. */
  hasProject: boolean;
}

/** Whether a step is in this person's tour. */
export function applies(step: TourStep, who: TourAudience): boolean {
  return !(step.ownerOnly && !who.owner) && !(step.firstProject && who.hasProject);
}

/**
 * The step to show from `from` on, going `direction`, for this person
 * (TOADO-988): an owner's steps are passed over for anybody else, and making
 * the first project for a workspace that has one. Steps keep their numbers
 * whoever takes the tour, so stored progress names the same step after a
 * change of role. Past either end it returns the end.
 */
export function stepFor(tour: TourDefinition, from: number, direction: 1 | -1, who: TourAudience): number {
  let step = from;
  while (step >= 0 && step < tour.steps.length && !applies(tour.steps[step]!, who)) step += direction;
  return step;
}

/**
 * The step Back goes to from `step` (TOADO-988): the one before, passing over
 * a step already met (the page it leads to is the one the person is on, or
 * the window it opens is open), which would send them forward again. With no
 * step before it in this person's tour, `step` itself.
 */
export function stepBack(tour: TourDefinition, step: number, who: TourAudience, pathname: string, present: (anchor: string) => boolean): number {
  let back = stepFor(tour, step - 1, -1, who);
  while (back > 0 && metAlready(tour.steps[back]!, pathname, present)) back = stepFor(tour, back - 1, -1, who);
  return back < 0 ? step : back;
}

/**
 * Where a step is in this person's tour, counting only the steps in it
 * (Jason, 28 Oct): "3 of 26" for someone with a project, where the first
 * project's two steps are left out, rather than "5 of 28" with a gap.
 */
export function stepNumber(tour: TourDefinition, step: number, who: TourAudience): { index: number; total: number } {
  let index = 0;
  let total = 0;
  tour.steps.forEach((candidate, at) => {
    if (!applies(candidate, who)) return;
    if (at < step) index += 1;
    total += 1;
  });
  return { index, total };
}

/**
 * Where a tour resumed at `step` starts (Jason, 28 Oct): at the start of its
 * section, since what the section stands on (the sample ticket, opened) is
 * made again for each run.
 */
export function resumeAt(tour: TourDefinition, step: number): number {
  const section = tour.steps[step]?.section;
  if (!section) return step;
  let first = step;
  while (first > 0 && tour.steps[first - 1]!.section === section) first -= 1;
  return first;
}

/**
 * The page a step is on, as an address to go to (Jason, 28 Oct): its `open`,
 * or else its route, with the project and ticket filled in. Null when the
 * tour does not know one it needs, or the route has a part it cannot fill.
 */
export function stepPath(step: TourStep, ids: { projectId: string | null; ticketId: string | null }): string | null {
  const pattern = step.open ?? step.route;
  if (!pattern) return null;
  let missing = false;
  const filled = pattern
    .replace(/\/\*$/, '')
    .replace(/:([A-Za-z]+)/g, (_, name: string) => {
      const value = name === 'projectId' ? ids.projectId : name === 'ticketId' ? ids.ticketId : null;
      if (!value) missing = true;
      return encodeURIComponent(value ?? '');
    });
  return missing ? null : filled || '/';
}

/** Whether the person is at `path` already: the same page, and every `?` part it names the same. */
export function atPath(path: string, pathname: string, search: string): boolean {
  const [want, query = ''] = path.split('?');
  if (want !== pathname) return false;
  const have = new URLSearchParams(search);
  return [...new URLSearchParams(query)].every(([key, value]) => have.get(key) === value);
}

/** Whether a step's wait is met as things are now: its page reached, or its control already there. */
function metAlready(step: TourStep, pathname: string, present: (anchor: string) => boolean): boolean {
  const wait = step.waitFor;
  if (!wait) return false;
  // Not a step that takes the person to its own page first (Jason, 28 Oct):
  // Back to "Click the settings icon" goes to the board, where it is not met.
  if ('route' in wait) return !step.open && matchPath(wait.route, pathname) !== null;
  return 'event' in wait && wait.present !== undefined && present(wait.present);
}

/** Where the person is now, for scoped tours. */
export interface TourContext {
  companyId: string | null;
  projectId: string | null;
}

export type TourOffer =
  | { kind: 'start' }
  | { kind: 'resume'; step: number }
  | { kind: 'replay' }
  /** The tour changed since they took it: replayed from the start, not resumed. */
  | { kind: 'changed' }
  /** In progress somewhere else: started again here, never resumed against the wrong context. */
  | { kind: 'elsewhere' };

/** What a tour offers, from the person's stored progress and where they are now. */
export function tourOffer(tour: TourDefinition, stored: TourProgress | undefined, here: TourContext): TourOffer {
  // Declined without a step seen: new to them, whatever version they turned down.
  if (!stored || stored.state === 'declined') return { kind: 'start' };
  if (stored.version !== tour.version) return { kind: 'changed' };
  if (stored.state !== 'in_progress') return { kind: 'replay' };
  const sameWorkspace = !!here.companyId && stored.scope?.companyId === here.companyId;
  if (tour.scope === 'workspace' && !sameWorkspace) return { kind: 'elsewhere' };
  // A project tour stored before it reached a project resumes anywhere in its
  // workspace, and binds to the first project it is taken to.
  // A page with no project of its own (workspace settings) is not another project (TOADO-988).
  if (tour.scope === 'project' && !(sameWorkspace && (stored.scope?.projectId === undefined || here.projectId === null || stored.scope.projectId === here.projectId))) return { kind: 'elsewhere' };
  // A step past the end would mean the steps changed without the version: start over.
  if (stored.step >= tour.steps.length) return { kind: 'changed' };
  return { kind: 'resume', step: stored.step };
}

/**
 * Whether a tour is offered on its own here (TOADO-981, 988): never stored, or
 * stored only in another workspace, since a workspace tour is new in each
 * workspace a person joins (Jason, 28 Oct).
 */
export function offeredHere(stored: TourProgress | undefined, here: TourContext): boolean {
  if (!stored) return true;
  return !!here.companyId && !!stored.scope?.companyId && stored.scope.companyId !== here.companyId;
}

/** The scope a write stores for this tour, here. */
export function tourScope(tour: TourDefinition, here: TourContext): TourProgress['scope'] {
  if (tour.scope === 'none' || !here.companyId) return undefined;
  if (tour.scope === 'project' && here.projectId) return { companyId: here.companyId, projectId: here.projectId };
  return { companyId: here.companyId };
}

/**
 * A tour the e2e suite runs the mechanics against, on the board's own
 * controls. Present only when MSW serves the app and a spec asks for it, so
 * no real person ever sees it and the server never hears of it.
 */
const BOARD = '/projects/:projectId/board';

export const FIXTURE_TOUR: TourDefinition = {
  id: 'fixture',
  version: 1,
  title: 'Fixture tour',
  scope: 'workspace',
  steps: [
    { anchor: 'home.get-going', route: '/', title: 'Get going', body: 'These steps set Toado up.' },
    { anchor: 'board.filter', route: BOARD, routeHint: 'Open a project\'s board to carry on.', title: 'Filter', body: 'Filter narrows the board to the tickets you want.' },
    { anchor: 'board.views', route: BOARD, title: 'Views', body: 'Pick another view to carry on.', waitFor: { click: true } },
    { anchor: 'board.not-on-this-page', route: BOARD, title: 'Missing', body: 'This control is not on the board.' },
    { anchor: 'board.search', route: BOARD, title: 'Search', body: 'Search finds tickets in this project.' },
  ],
};

/**
 * The workspace, for someone new to it (TOADO-981, 988): the board's columns,
 * where tickets come from, the views and Filter, each tried rather than read
 * about, search, then the sample ticket the tour makes, its Session and
 * DevTools, the project's settings section by section, and connecting Claude
 * Code. A step whose control is not on the page (a viewer's board with no
 * sample, say) says so and can be skipped; every other step goes on by Next,
 * which does what the step asks when the person has not (Jason, 28 Oct).
 *
 * A step asking for a click moves on as it lands (`advanceWhenDone`), and
 * the steps inside the ticket show over it (`overModal`).
 */
const TICKET = '/tickets/:ticketId';
const PROJECT_SETTINGS = '/projects/:projectId/settings';
const WORKSPACE_SETTINGS = '/company/settings/*';
const settingsTab = (tab: string) => `/projects/:projectId/settings?tab=${tab}`;

/**
 * The workspace tour (Jason, 28 Oct): offered once in each workspace, on its
 * first board, and walked on a sample ticket it makes for the purpose. Its id
 * stays `board`, so progress already stored keeps its meaning.
 */
export const BOARD_TOUR: TourDefinition = {
  id: 'board',
  version: 5,
  title: 'Workspace tour',
  scope: 'workspace',
  sample: true,
  offer: { route: BOARD, body: 'Take the tour: your board, a sample ticket, the project\'s settings and connecting Claude Code.' },
  steps: [
    // A workspace with no project makes its first one with the dashboard's own
    // Create project button and dialog, and the tour goes on on its board (Jason, 28 Oct).
    {
      anchor: 'home.create-project',
      route: '/',
      open: '/',
      firstProject: true,
      section: 'first-project',
      title: 'Create your first project',
      body: 'A project is a board of tickets, usually one for each site or app you work on. Click Create project to make yours.',
      waitFor: { click: true },
      advanceWhenDone: true,
    },
    {
      anchor: 'create-project.form',
      route: '/',
      overModal: true,
      firstProject: true,
      section: 'first-project',
      reopenHint: 'Click Create project again to carry on with the tour.',
      title: 'Name it and say where it lives',
      body: 'Give it a name, and the address of the site it covers, so captures from that site land on its board. Create it, and the tour carries on there.',
      waitFor: { route: BOARD },
      advanceWhenDone: true,
      nextClicks: 'create-project.submit',
      backClicks: 'create-project.cancel',
      waitingHint: 'Type a name first, then click Create project or press Next.',
    },
    {
      anchor: 'board.columns',
      route: BOARD,
      routeHint: 'Open a project\'s board to start the tour.',
      title: 'Columns are your workflow',
      body: 'Each column is a stage a ticket goes through, from left to right. Drag a card to another column to move it on.',
    },
    {
      anchor: 'tour.sources',
      centered: true,
      route: BOARD,
      title: 'Where tickets come from',
      body: 'Most tickets arrive on their own: captures and walkthrough recordings from the Chrome extension, and reports from the bug collector widget on your own site. You can also add one by hand, with New ticket at the top left or Add ticket at the foot of any column.',
      art: 'sources',
    },
    {
      anchor: 'board.views',
      route: BOARD,
      title: 'Four ways to look',
      body: 'Board, Cards, Single and Table show the same tickets laid out differently. Click Table to see one.',
      // Not the view already chosen, which changes nothing (Jason, 28 Oct).
      waitFor: { click: true, ignore: '[aria-selected="true"]' },
      advanceWhenDone: true,
      nextClicks: 'board.views.table',
    },
    {
      // The whole switcher, where the view they are on shows as chosen, rather
      // than Board alone, which read as the one they were on (Jason, 28 Oct).
      anchor: 'board.views',
      route: BOARD,
      title: 'Back to the board',
      body: 'You are on {selected} now: the same tickets, laid out another way. Each project keeps the view you pick. Click Board to go back.',
      waitFor: { click: 'board.views.board' },
      advanceWhenDone: true,
      backClicks: 'board.views.board',
    },
    {
      anchor: 'board.filter',
      route: BOARD,
      title: 'Narrow it down',
      body: 'Click Filter to see what you can narrow the board to.',
      waitFor: { click: true },
      advanceWhenDone: true,
    },
    {
      anchor: 'filter.panel',
      route: BOARD,
      overModal: true,
      title: 'Everything you can filter by',
      body: 'Type, column, assignee, who reported it, and more. The quick filters beside Filter on the board are one click each. Next closes this.',
      nextClicks: 'filter.cancel',
      backClicks: 'filter.cancel',
    },
    {
      anchor: 'board.search',
      route: BOARD,
      title: 'Find a ticket',
      body: 'Search this project\'s tickets by title and description. Press / to jump here from anywhere on the board.',
    },
    {
      anchor: 'board.sample-ticket',
      route: BOARD,
      title: 'Open the sample ticket',
      body: 'This is an example ticket, the kind the extension makes: a screenshot of the page, with its console, network requests and errors attached. Click it to open it.',
      waitFor: { route: TICKET },
      advanceWhenDone: true,
      art: 'capture',
      section: 'sample',
    },
    {
      anchor: 'ticket.description',
      route: TICKET,
      open: TICKET,
      overModal: true,
      section: 'sample',
      title: 'What happened',
      body: 'The reporter\'s words, or the write-up of what they said while recording. On your own tickets, edit it here like any text.',
    },
    {
      anchor: 'ticket.comments',
      route: TICKET,
      open: TICKET,
      overModal: true,
      section: 'sample',
      title: 'Talk it over',
      body: 'Comments are for your team and for your agent: when Claude Code works a ticket, its notes land here.',
    },
    {
      anchor: 'ticket.session',
      route: TICKET,
      open: TICKET,
      overModal: true,
      title: 'The whole visit',
      body: 'Session lays out what the person did, step by step, beside what the page said. Click Session to open it.',
      waitFor: { event: 'tour:debug-opened', present: 'debug.session' },
      advanceWhenDone: true,
      section: 'sample',
    },
    {
      anchor: 'debug.session',
      route: TICKET,
      overModal: true,
      section: 'sample',
      reopenHint: 'Open Session again to carry on with the tour.',
      title: 'Session',
      body: 'Pages and clicks in order, with each console line, failed request and error where it happened. Copy reproduction turns it into steps and a Playwright test.',
      art: 'session',
    },
    {
      anchor: 'debug.network',
      route: TICKET,
      overModal: true,
      section: 'sample',
      reopenHint: 'Open Session again to carry on with the tour.',
      title: 'One tab for each',
      body: 'Network has every request with its headers and timing, and Console, Errors and Performance sit beside it. A row in Session that points at one opens its tab.',
    },
    {
      anchor: 'tour.to-settings',
      centered: true,
      route: TICKET,
      open: TICKET,
      overModal: true,
      section: 'sample',
      title: 'Next: the project\'s settings',
      body: 'That is everything a ticket holds. Next closes the ticket and takes you back to the board, where each project keeps its settings.',
    },
    {
      anchor: 'board.settings',
      route: BOARD,
      open: BOARD,
      title: 'Click the settings icon',
      body: 'Each project has its own settings: its columns, the sites it covers, the bug collector and Jira. Click the settings icon to open them.',
      waitFor: { route: PROJECT_SETTINGS },
      advanceWhenDone: true,
    },
    {
      anchor: 'settings.page.columns',
      route: PROJECT_SETTINGS,
      open: settingsTab('columns'),
      title: 'Columns',
      body: 'Add, rename and reorder this board\'s columns here. Board workflow, under it in the list, says what each one is for.',
    },
    {
      anchor: 'settings.page.urls',
      route: PROJECT_SETTINGS,
      open: settingsTab('urls'),
      title: 'URL patterns',
      body: 'The sites this project covers. On any page that matches, the extension files captures into this project.',
      art: 'route',
    },
    {
      anchor: 'widget.snippet',
      route: PROJECT_SETTINGS,
      open: settingsTab('widget'),
      ownerOnly: true,
      title: 'Bug collector: install',
      body: 'The bug collector puts a report button on your own site for your users. Paste this snippet into your site, before the closing body tag.',
      art: 'widget',
    },
    {
      anchor: 'widget.sites',
      route: PROJECT_SETTINGS,
      open: settingsTab('widget'),
      ownerOnly: true,
      title: 'Where it may run',
      body: 'List the sites the bug collector is allowed on. Anywhere else it stays off, even with the snippet in place.',
    },
    {
      anchor: 'widget.appearance-groups',
      route: PROJECT_SETTINGS,
      open: settingsTab('widget-appearance'),
      ownerOnly: true,
      title: 'Appearance',
      body: 'The launcher button, the colours and styles, a panel or a modal, and your branding. Match my site takes the colours from your site for you.',
    },
    {
      anchor: 'widget.preview',
      route: PROJECT_SETTINGS,
      open: settingsTab('widget-appearance'),
      ownerOnly: true,
      title: 'See it as you change it',
      body: 'The preview draws each of the bug collector\'s screens with your changes, saved or not.',
    },
    {
      anchor: 'widget.capture-groups',
      route: PROJECT_SETTINGS,
      open: settingsTab('widget-capture'),
      ownerOnly: true,
      title: 'Capture',
      body: 'What a report can bring: a recording of the page, pointing at an element, the visitor\'s own words, and the page details, its console, network and errors.',
    },
    {
      anchor: 'settings.page.jira',
      route: PROJECT_SETTINGS,
      open: settingsTab('jira'),
      ownerOnly: true,
      title: 'Jira export',
      body: 'Map this project to a Jira project, and send tickets there by hand or on their own, with their pictures and video.',
    },
    {
      anchor: 'company-settings.page',
      route: WORKSPACE_SETTINGS,
      open: '/company/settings/mcp-setup',
      title: 'Connect Claude Code',
      body: 'Claude Code reaches Toado through the MCP connector, set up once for the workspace. Add it with the command here, and Claude can read a ticket and its capture, fix it, and move it on the board.',
      art: 'agent',
    },
    {
      anchor: 'company-settings.page',
      route: WORKSPACE_SETTINGS,
      open: '/company/settings/pre-built-skill',
      title: 'Download the skill',
      body: 'The Toado skill tells Claude Code how to work your board: which ticket to take, what to leave on it, and when to move it on. Download it here.',
    },
    {
      anchor: 'company-settings.page',
      route: WORKSPACE_SETTINGS,
      open: '/company/settings/extension',
      title: 'Install the extension',
      body: 'Captures and walkthrough recordings come from the Chrome extension. Install it and you are ready to file your first ticket. Get started, top right, has this tour again.',
      art: 'extension',
    },
  ],
};

const MCP_SETUP = '/me/settings/mcp-setup';

/**
 * Connecting an agent (TOADO-974): pick the client, connect it in the client
 * itself, and check it with a read Toado sees.
 * Every step is about this page, so the tour needs no project and no board
 * tour first. The client's own steps are the page's (ClientPanel), never a
 * second copy here, and nothing a step says is a token or a header.
 */
export const MCP_TOUR: TourDefinition = {
  id: 'mcp-setup',
  version: 1,
  title: 'Connect an agent',
  scope: 'none',
  steps: [
    {
      anchor: 'mcp.clients',
      route: MCP_SETUP,
      open: MCP_SETUP,
      title: 'Pick your agent',
      body: 'Choose the client your agent runs in. Each connects a little differently, and its own steps and the address to copy are just below.',
      art: 'agent',
    },
    {
      anchor: 'mcp.client-panel',
      route: MCP_SETUP,
      title: 'Connect it in your client',
      body: 'Copy what this client needs and follow its steps there. The sign-in happens in your client and the browser tab it opens, where you approve the access your agent gets: Toado cannot press anything in another app for you. Read-only is enough to check it works.',
    },
    {
      anchor: 'mcp.check',
      route: MCP_SETUP,
      title: 'Check it works',
      body: 'Press Check, then paste the prompt into your agent. It reads one project and changes nothing. Only a read Toado sees counts as working; if you say it worked, the page keeps that as your word.',
    },
    {
      anchor: 'mcp.troubleshooting',
      route: MCP_SETUP,
      title: 'If it does not work',
      body: 'The usual causes are here: a client that needs restarting after its config changes, access approved without the project, or a sign-in to another account. Get started, top right, has this tour again.',
    },
  ],
};

const EXTENSION_SETUP = '/me/settings/extension';
/**
 * The same page under personal or workspace settings (review round 1): either counts as being on
 * it. Every step opens the personal one (review round 2), so a tour resumed from another page has
 * somewhere to go: the route pattern alone cannot be turned into an address.
 */
const EXTENSION_SETUP_ANY = '/:area/settings/extension';

/**
 * Setting up the extension (TOADO-973): install it, connect it in Chrome,
 * see that Toado sees it, and record a first walkthrough. Chrome's store, its
 * toolbar and its share picker are not Toado's pages, so those steps say what
 * to do there and come back here; they never claim to point at them. Nothing
 * here records, connects or files anything.
 */
export const EXTENSION_TOUR: TourDefinition = {
  id: 'extension-setup',
  version: 1,
  title: 'Set up the extension',
  scope: 'none',
  steps: [
    {
      anchor: 'extension.install',
      route: EXTENSION_SETUP_ANY,
      open: EXTENSION_SETUP,
      title: 'Install it',
      body: 'Install Toado from the Chrome Web Store, or try the next version from here where it is offered. The store opens in another tab: come back here once it is added. Pressing Install connects nothing by itself.',
      art: 'extension',
    },
    {
      anchor: 'extension.connect',
      route: EXTENSION_SETUP_ANY,
      open: EXTENSION_SETUP,
      title: 'Connect it in Chrome',
      body: 'Press the Toado icon in Chrome\'s toolbar (pin it from the puzzle-piece menu so it stays there), then Connect to Toado and approve. That happens in Chrome, not on this page, so the tour cannot point at it.',
    },
    {
      anchor: 'extension.status',
      route: EXTENSION_SETUP_ANY,
      open: EXTENSION_SETUP,
      title: 'See that Toado sees it',
      body: 'This says what Toado sees for this account. Press Check again once you have connected. If the extension says it is connected and this does not, it may be connected to another Toado account: its Settings, under Account, says which.',
    },
    {
      anchor: 'extension.first-recording',
      route: EXTENSION_SETUP_ANY,
      open: EXTENSION_SETUP,
      title: 'Your first walkthrough',
      body: 'Record from Toado\'s panel on a page you may test. Start a ticket before you talk about it: Alt+click what you mean. No pin ticket starts one with nothing to point at. Stop, and the tickets file themselves.',
    },
    {
      anchor: 'extension.troubleshooting',
      route: EXTENSION_SETUP_ANY,
      open: EXTENSION_SETUP,
      title: 'If something goes wrong',
      body: 'The usual problems and their fixes are here. Get started, top right, has this tour again.',
    },
  ],
};

function e2eWants(key: string): boolean {
  if (import.meta.env.VITE_USE_MSW !== '1') return true;
  try { return window.localStorage.getItem(key) === '1'; } catch { return false; }
}

/** Every tour there is. */
export function registeredTours(): readonly TourDefinition[] {
  // Under MSW the fixture tour is there only when a spec asks for it.
  const fixture = import.meta.env.VITE_USE_MSW === '1' && e2eWants('vd-e2e-fixture-tour');
  return fixture ? [FIXTURE_TOUR, BOARD_TOUR, MCP_TOUR, EXTENSION_TOUR] : [BOARD_TOUR, MCP_TOUR, EXTENSION_TOUR];
}

/**
 * Whether tours are offered on their own (TOADO-981). Under MSW only when a
 * spec asks, so the rest of the suite meets every board as it always has.
 */
export function offersWanted(): boolean {
  return e2eWants('vd-e2e-tour-offers');
}

/** Tours are on unless the build turns them off; stored progress is kept either way. */
export const TOURS_ENABLED = import.meta.env.VITE_TOURS !== '0';
